import { useMemo } from "react";
import { ColumnDef } from "@tanstack/react-table";
import { Type } from "@themes/pollinator/ui/Type";
import { Badge } from "@themes/pollinator/ui/Badge";
import { DataTable } from "@themes/pollinator/ui/DataTable";
import type { InvitationRecord } from "@core/features/invitation";

export type Props = {
  records: InvitationRecord[];
  /** Show invitation code column (for multi-invitation views) */
  showInvitationColumn?: boolean;
};

/**
 * Table displaying invitation records with name, status, and date.
 */
export function InvitationRecordsTable({
  records,
  showInvitationColumn = false,
}: Props) {
  const columns = useMemo<ColumnDef<InvitationRecord>[]>(() => {
    const baseColumns: ColumnDef<InvitationRecord>[] = [];

    // Optionally add invitation code column first
    if (showInvitationColumn) {
      baseColumns.push({
        accessorKey: "invitation_shortcode",
        header: () => "Invitation",
        cell: ({ row }) => (
          <Type variant="caption" className="font-mono text-brand-800">
            {row.original.invitation_shortcode || "—"}
          </Type>
        ),
      });
    }

    // Invitee column
    baseColumns.push({
      accessorKey: "sent_to_email",
      header: () => "Invitee",
      cell: ({ row }) => {
        const record = row.original;
        const isClaimed = record.claimed_status === "claimed";

        // Use claimed_by info if claimed, otherwise use sent_to info
        const name = isClaimed
          ? [record.claimed_by?.first_name, record.claimed_by?.last_name]
              .filter(Boolean)
              .join(" ")
          : [record.sent_to_first_name, record.sent_to_last_name]
              .filter(Boolean)
              .join(" ");

        const email = isClaimed
          ? record.claimed_by?.email
          : record.sent_to_email;

        return (
          <div className="flex flex-col">
            {name && (
              <Type variant="h6" className="truncate max-w-xs">
                {name}
              </Type>
            )}
            <Type variant="caption" className="text-brand-700">
              {email}
            </Type>
          </div>
        );
      },
    });

    // Status column
    baseColumns.push({
      accessorKey: "claimed_status",
      header: () => "Status",
      cell: ({ row }) => {
        const status = row.original.claimed_status;

        if (status === "claimed") {
          return (
            <Badge className="border-success-600 text-success-700 bg-success-50">
              Joined
            </Badge>
          );
        }

        if (status === "cancelled") {
          return (
            <Badge className="border-brand-400 text-brand-600 bg-brand-50">
              Cancelled
            </Badge>
          );
        }

        return (
          <Badge className="border-warning-600 text-warning-700 bg-warning-50">
            Pending
          </Badge>
        );
      },
    });

    // Date column
    baseColumns.push({
      accessorKey: "created_at",
      header: () => "Date",
      cell: ({ row }) => {
        const record = row.original;
        const isClaimed = record.claimed_status === "claimed";

        // Show claimed_at for claimed records, created_at for pending
        const dateStr = isClaimed ? record.claimed_at : record.created_at;

        if (!dateStr) return null;

        const date = new Date(dateStr);
        const formatted = date.toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
          year: "numeric",
        });

        return (
          <Type variant="caption" className="text-brand-700">
            {isClaimed ? `Joined ${formatted}` : `Sent ${formatted}`}
          </Type>
        );
      },
    });

    return baseColumns;
  }, [showInvitationColumn]);

  return (
    <DataTable
      data={records}
      columns={columns}
      isWithSearch={false}
      initialPageSize={10}
    />
  );
}

export default InvitationRecordsTable;
